import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { formatarMoeda } from '../../lib/formatos'
import { repetirPedido } from '../../pedidos/repetir'
import { ROTULO_ESTAGIO, CLASSE_ESTAGIO } from '../../pedidos/estagio'
import { montarHistorico, ultimos12Meses, type FiltroHistorico } from '../../clientes/historico'

// Aba Historico do cliente, redesenhada pelo print dela (11/10). As contas
// moram em clientes/historico.ts. ⚠ Este componente vive DENTRO do <form> do
// cadastro: todo botao e type="button" e o Enter da busca nao envia -- senao
// paginar ou buscar acionaria o Salvar.

const POR_PAGINA = 5
// Paleta categorica validada (dataviz, 5 fatias: daltonismo e visao normal ok).
// A legenda traz nome e valor: a cor nunca e a unica pista.
const CORES_TOP = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4']
const COR_BARRA = '#0f766e'

// 'AAAA-MM-DD' -> 'DD/MM/AAAA' sem passar por Date (licao do fuso UTC).
const dataBR = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`
const compacto = (v: number) =>
  v >= 1000 ? `${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k` : String(Math.round(v))
const numero = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })

function Icone({ d, className = '' }: { d: string; className?: string }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={className}>
      <path d={d} />
    </svg>
  )
}

// Ordem da aba (ela, 11/10): filtros e totais em cima, graficos e a LISTA de
// compras no fim. "Precos negociados" saiu do app no mesmo dia (decisao dela).
export default function HistoricoCompras({ clienteId }: { clienteId: string }) {
  const navegar = useNavigate()
  // Abre nos ultimos 12 meses (decisao dela 11/10): com o ano atual, em
  // janeiro a aba abria vazia para quem comprou em novembro.
  const [filtro, setFiltro] = useState<FiltroHistorico>(() => ({
    representadaId: null, ...ultimos12Meses(new Date()), busca: '',
  }))
  const [pagina, setPagina] = useState(1)
  const [barraTocada, setBarraTocada] = useState<number | null>(null)

  const dados = useLiveQuery(async () => {
    const pedidos = await banco.pedidos.where('cliente_id').equals(clienteId).toArray()
    const ids = pedidos.map((p) => p.id)
    const subPedidos = await banco.sub_pedidos.where('pedido_id').anyOf(ids).toArray()
    const itens = await banco.itens_pedido.where('sub_pedido_id').anyOf(subPedidos.map((s) => s.id)).toArray()
    const produtos = new Map(
      (await banco.produtos.bulkGet([...new Set(itens.map((i) => i.produto_id))]))
        .filter((p) => p !== undefined)
        .map((p) => [p!.id, p!.descricao]),
    )
    const todasRepresentadas = await banco.representadas.toArray()
    const representadas = new Map(todasRepresentadas.map((r) => [r.id, r.nome]))
    return { pedidos, subPedidos, itens, produtos, representadas }
  }, [clienteId])

  const historico = useMemo(
    () => dados && montarHistorico({ clienteId, ...dados, filtro }),
    [dados, filtro, clienteId],
  )
  // So as fornecedoras com que este cliente ja comprou -- a lista nao enche
  // de marca que nunca vendeu para ele.
  const fornecedoras = useMemo(() => {
    if (!dados) return []
    const ids = new Set(dados.subPedidos.filter((s) => s.apagado_em === null).map((s) => s.representada_id))
    return [...ids]
      .map((rid) => ({ id: rid, nome: dados.representadas.get(rid) ?? '—' }))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  }, [dados])

  function mudar(parcial: Partial<FiltroHistorico>) {
    setFiltro((f) => ({ ...f, ...parcial }))
    setPagina(1)
  }

  async function aoRepetir() {
    const resultado = await repetirPedido(clienteId)
    if (resultado.pulados > 0) {
      window.alert(`${resultado.pulados} item(ns) não puderam ser repetidos (produto inativo ou sem preço).`)
    }
    navegar(`/pedidos/${resultado.pedidoId}`)
  }

  if (!historico) return null
  const totalPaginas = Math.max(1, Math.ceil(historico.linhas.length / POR_PAGINA))
  const paginaAtual = Math.min(pagina, totalPaginas)
  const visiveis = historico.linhas.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA)
  const temCompra = dados!.pedidos.some((p) => p.apagado_em === null && p.status !== 'aberto')
  const estiloCampo = 'w-full border border-slate-300 rounded-lg px-2 py-2 text-sm bg-white'
  const estiloRotulo = 'block text-xs text-slate-600 mb-1'

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4 space-y-4" data-testid="historico-compras">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-purple-50 text-purple-700 flex items-center justify-center shrink-0">
            <Icone d="M3 4h2l2.4 11h10.2L20 7H6.2M9 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM17 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-slate-800">Histórico de compras</h3>
            <p className="text-sm text-slate-500">Visualize todas as compras realizadas por este cliente.</p>
          </div>
        </div>
        {temCompra && (
          <button type="button" onClick={() => void aoRepetir()} className="text-sm text-teal-700 underline">
            Repetir último pedido
          </button>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="hist-forn" className={estiloRotulo}>Fornecedor</label>
            <select id="hist-forn" value={filtro.representadaId ?? ''} className={estiloCampo}
              onChange={(e) => mudar({ representadaId: e.target.value || null })}>
              <option value="">Todos</option>
              {fornecedoras.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="hist-busca" className={estiloRotulo}>Buscar</label>
            <input id="hist-busca" type="search" value={filtro.busca} placeholder="Nº do pedido, produto…"
              className={estiloCampo}
              onChange={(e) => mudar({ busca: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault() }} />
          </div>
          <div className="col-span-2">
            <span className={estiloRotulo}>Período</span>
            <div className="flex items-center gap-2">
              <input type="date" aria-label="Período de" value={filtro.de} className={estiloCampo}
                onChange={(e) => e.target.value && mudar({ de: e.target.value })} />
              <span className="text-slate-400" aria-hidden="true">→</span>
              <input type="date" aria-label="Período até" value={filtro.ate} className={estiloCampo}
                onChange={(e) => e.target.value && mudar({ ate: e.target.value })} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2" data-testid="historico-totais">
          <div className="rounded-xl bg-emerald-50 p-2">
            <p className="text-[11px] leading-tight text-emerald-800">Total de compras</p>
            <p className="font-bold text-emerald-900 text-sm break-words">{formatarMoeda(historico.totais.valor)}</p>
          </div>
          <div className="rounded-xl bg-blue-50 p-2">
            <p className="text-[11px] leading-tight text-blue-800">Quantidade de pedidos</p>
            <p className="font-bold text-blue-900 text-lg">{historico.totais.pedidos}</p>
          </div>
          <div className="rounded-xl bg-orange-50 p-2">
            <p className="text-[11px] leading-tight text-orange-800">Média de itens por pedido</p>
            <p className="font-bold text-orange-900 text-lg">{numero(historico.totais.mediaItens)}</p>
          </div>
        </div>

      </div>

      {historico.totais.valor > 0 && (
        <>
          <ComprasPorMes meses={historico.meses} periodo={historico.rotuloPeriodo} tocada={barraTocada} aoTocar={setBarraTocada} />
          <TopProdutos itens={historico.topProdutos} outros={historico.outros} periodo={historico.rotuloPeriodo} />
        </>
      )}

      {/* Visual pelo print dela (11/10): fornecedora em destaque, codigo e
          data discretos, itens x total na mesma linha, divisoria leve. */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="lista-de-compras">
        <div className="flex items-baseline justify-between px-4 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-800">Lista de compras</h3>
          {historico.linhas.length > 0 && (
            <span className="text-sm text-slate-500">
              {historico.linhas.length} {historico.linhas.length === 1 ? 'registro' : 'registros'}
            </span>
          )}
        </div>
        {historico.linhas.length === 0 ? (
          <p className="text-sm text-slate-500 px-4 py-4">
            {temCompra ? 'Nenhuma compra com esses filtros.' : 'Nenhuma compra ainda.'}
          </p>
        ) : (
          <>
            <ul className="divide-y divide-slate-100" data-testid="historico-linhas">
              {visiveis.map((l) => (
                <li key={l.id}>
                  <button type="button" onClick={() => navegar(`/pedidos/${l.pedidoId}`)}
                    className="w-full text-left px-4 py-4 flex items-center gap-3 active:bg-slate-50" data-testid="linha-compra">
                    <span className="flex-1 min-w-0 space-y-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-base font-bold text-slate-900 truncate">{l.fornecedora}</span>
                        <span className={`text-xs rounded-full px-2.5 py-0.5 shrink-0 ${CLASSE_ESTAGIO[l.status]}`}>{ROTULO_ESTAGIO[l.status]}</span>
                      </span>
                      <span className="block text-xs text-slate-500">{l.numero} · {dataBR(l.data)}</span>
                      <span className="flex items-baseline justify-between gap-2 pt-0.5">
                        <span className="text-sm text-slate-500">{numero(l.itens)} {l.itens === 1 ? 'item' : 'itens'}</span>
                        <span className={`text-base font-bold ${l.status === 'cancelado' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                          {formatarMoeda(l.total)}
                        </span>
                      </span>
                    </span>
                    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round" className="text-slate-400 shrink-0" aria-hidden="true">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between gap-2 flex-wrap px-4 py-3 border-t border-slate-100">
              <p className="text-sm text-slate-500" data-testid="historico-mostrando">
                Mostrando {visiveis.length} de {historico.linhas.length} registros
              </p>
              {totalPaginas > 1 && (
                <nav className="flex items-center gap-1" aria-label="Páginas do histórico">
                  <button type="button" aria-label="Página anterior" disabled={paginaAtual === 1}
                    onClick={() => setPagina(paginaAtual - 1)}
                    className="w-8 h-8 rounded-lg border border-slate-200 text-teal-700 disabled:opacity-40">‹</button>
                  {Array.from({ length: totalPaginas }, (_, i) => i + 1)
                    .filter((n) => Math.abs(n - paginaAtual) <= 2 || n === 1 || n === totalPaginas)
                    .map((n) => (
                      <button key={n} type="button" onClick={() => setPagina(n)} aria-current={n === paginaAtual ? 'page' : undefined}
                        className={`w-8 h-8 rounded-lg text-sm ${n === paginaAtual ? 'bg-teal-700 text-white' : 'border border-slate-200 text-slate-700'}`}>
                        {n}
                      </button>
                    ))}
                  <button type="button" aria-label="Próxima página" disabled={paginaAtual === totalPaginas}
                    onClick={() => setPagina(paginaAtual + 1)}
                    className="w-8 h-8 rounded-lg border border-slate-200 text-teal-700 disabled:opacity-40">›</button>
                </nav>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function CabecalhoGrafico({ d, titulo, subtitulo, cor }: { d: string; titulo: string; subtitulo: string; cor: string }) {
  return (
    <div className="flex items-center gap-3">
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cor}`}><Icone d={d} /></span>
      <div className="min-w-0">
        <h3 className="font-bold text-slate-800">{titulo}</h3>
        <p className="text-xs text-slate-500">{subtitulo}</p>
      </div>
    </div>
  )
}

// Barras SVG, sem biblioteca (o precache do app ja esta perto do limite).
// Uma serie so: sem legenda, o titulo diz o que e. Valor em cima de cada
// barra, como no print; tocar destaca o mes e mostra o valor exato.
function ComprasPorMes({
  meses, periodo, tocada, aoTocar,
}: {
  meses: { chave: string; rotulo: string; valor: number }[]
  periodo: string
  tocada: number | null
  aoTocar: (i: number | null) => void
}) {
  const maximo = Math.max(...meses.map((m) => m.valor), 1)
  const n = Math.max(meses.length, 1)
  const L = 320, A = 170, base = 140, topo = 22, larg = L / n
  // Periodo longo: o nome do mes de 2 em 2 (ou mais) e sem o valor em cima,
  // que nao cabe -- o valor exato aparece ao tocar na barra.
  const passo = Math.ceil(n / 12)
  const mostrarValor = n <= 12
  const curto = (rotulo: string) => (n <= 12 ? rotulo.slice(0, 3) : rotulo)
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3" data-testid="grafico-mes">
      <CabecalhoGrafico d="M5 20V10M12 20V4M19 20v-7" titulo="Compras por mês"
        subtitulo={`Valor total das compras de ${periodo}.`} cor="bg-emerald-50 text-emerald-700" />
      <svg viewBox={`0 0 ${L} ${A}`} className="w-full h-auto" role="img"
        aria-label={`Compras por mês: ${meses.map((m) => `${m.rotulo} ${formatarMoeda(m.valor)}`).join(', ')}`}>
        <line x1="0" x2={L} y1={base} y2={base} stroke="#e2e8f0" strokeWidth="1" />
        {meses.map((m, i) => {
          const v = m.valor
          const h = v > 0 ? Math.max(3, ((base - topo) * v) / maximo) : 0
          const x = i * larg + larg * 0.2
          const w = larg * 0.6
          const y = base - h
          const raio = Math.min(4, w / 2)
          return (
            <g key={m.chave} onClick={() => aoTocar(tocada === i ? null : i)} className="cursor-pointer">
              <rect x={i * larg} y={0} width={larg} height={A} fill="transparent" />
              {h > 0 && (
                <path d={`M${x},${base} V${y + raio} Q${x},${y} ${x + raio},${y} H${x + w - raio} Q${x + w},${y} ${x + w},${y + raio} V${base} Z`}
                  fill={COR_BARRA} opacity={tocada === null || tocada === i ? 1 : 0.35} />
              )}
              {v > 0 && mostrarValor && (
                <text x={x + w / 2} y={y - 4} textAnchor="middle" fontSize="8.5" fill="#475569">{compacto(v)}</text>
              )}
              {i % passo === 0 && (
                <text x={x + w / 2} y={base + 14} textAnchor="middle" fontSize="9" fill="#64748b">{curto(m.rotulo)}</text>
              )}
              {n <= 12 && i > 0 && m.chave.endsWith('-01') && (
                <text x={x + w / 2} y={base + 25} textAnchor="middle" fontSize="8" fill="#94a3b8">{m.chave.slice(0, 4)}</text>
              )}
            </g>
          )
        })}
      </svg>
      {tocada !== null && meses[tocada] && (
        <p className="text-sm text-slate-700" data-testid="barra-tocada">
          {meses[tocada].rotulo}: <strong>{formatarMoeda(meses[tocada].valor)}</strong>
        </p>
      )}
    </div>
  )
}

// Rosca SVG com as fatias separadas por 2px da cor do fundo; o centro mostra o
// maior produto. A legenda e a fonte da leitura (nome + valor + %). O resto dos
// produtos entra como "Outros", em cinza: sem ele as 5 fatias enchiam o circulo
// e o desenho contradizia a % da legenda (code review 11/10).
const COR_OUTROS = '#cbd5e1'
function TopProdutos({
  itens: top, outros, periodo,
}: { itens: { nome: string; valor: number; fatia: number }[]; outros: number; periodo: string }) {
  const somaTop = top.reduce((s, i) => s + i.valor, 0)
  const total = somaTop + outros
  const itens = outros > 0
    ? [...top, { nome: 'Outros', valor: outros, fatia: total ? outros / total : 0 }]
    : top
  const cor = (i: number) => (i < top.length ? CORES_TOP[i] : COR_OUTROS)
  const R = 60, r = 38, c = 70
  let angulo = -Math.PI / 2
  const ponto = (raio: number, a: number) => `${c + raio * Math.cos(a)},${c + raio * Math.sin(a)}`
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-3" data-testid="grafico-top">
      <CabecalhoGrafico d="M12 3a9 9 0 1 0 9 9h-9z M14 2.5V10h7.5A8 8 0 0 0 14 2.5z" titulo="Top 5 produtos"
        subtitulo={`Mais comprados por valor, de ${periodo}.`} cor="bg-blue-50 text-blue-700" />
      <div className="flex items-center gap-3">
        <svg viewBox="0 0 140 140" className="w-32 h-32 shrink-0" role="img"
          aria-label={`Top produtos: ${itens.map((i) => `${i.nome} ${formatarMoeda(i.valor)}`).join(', ')}`}>
          {itens.length === 1 ? (
            <circle cx={c} cy={c} r={(R + r) / 2} fill="none" stroke={cor(0)} strokeWidth={R - r} />
          ) : (
            itens.map((item, i) => {
              const a0 = angulo
              const a1 = angulo + (item.valor / total) * Math.PI * 2
              angulo = a1
              const grande = a1 - a0 > Math.PI ? 1 : 0
              return (
                <path key={`${i}-${item.nome}`} fill={cor(i)} stroke="#ffffff" strokeWidth="2"
                  d={`M${ponto(R, a0)} A${R},${R} 0 ${grande} 1 ${ponto(R, a1)} L${ponto(r, a1)} A${r},${r} 0 ${grande} 0 ${ponto(r, a0)} Z`} />
              )
            })
          )}
          <text x={c} y={c - 2} textAnchor="middle" fontSize="11" fontWeight="700" fill="#1e293b">{compacto(itens[0]?.valor ?? 0)}</text>
          <text x={c} y={c + 12} textAnchor="middle" fontSize="10" fill="#64748b">
            {Math.round((itens[0]?.fatia ?? 0) * 100)}%
          </text>
        </svg>
        <ul className="space-y-1.5 min-w-0 flex-1" data-testid="legenda-top">
          {/* Indice na chave: um produto chamado "Outros" repetiria a da fatia cinza (code review 11/10, ponto 10). */}
          {itens.map((item, i) => (
            <li key={`${i}-${item.nome}`} className="flex gap-2 items-start text-xs">
              <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ background: cor(i) }} aria-hidden="true" />
              <span className="min-w-0">
                <span className="block text-slate-800 truncate">{item.nome}</span>
                <span className="block text-slate-500">{formatarMoeda(item.valor)} · {Math.round(item.fatia * 100)}%</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
